import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles, Thermometer, Wind, CheckCircle2, ChevronRight,
  Droplets, Wrench, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MaintenancePage = () => {
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    device_type: '',
    device_model: '',
    service_type: '',
    contact_email: '',
    contact_phone: '',
    preferred_date: ''
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/maintenance/services`);
      if (res.ok) {
        const data = await res.json();
        setServices(data.services || []);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectService = (service) => {
    setSelectedService(service);
    setFormData({ ...formData, service_type: service.id });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const res = await fetch(`${API_URL}/api/maintenance/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success('Solicitud de mantenimiento enviada');
        setShowForm(false);
      } else {
        toast.error('Error al enviar solicitud');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const getIcon = (serviceId) => {
    switch (serviceId) {
      case 'limpieza_basica':
      case 'limpieza_profunda':
        return Wind;
      case 'pasta_termica':
        return Thermometer;
      case 'limpieza_lector':
        return Droplets;
      default:
        return Wrench;
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="maintenance-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-sky-500 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="flex items-center gap-2 text-sm text-sky-200 mb-4">
            <Link to="/" className="hover:text-white">Inicio</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Mantenimiento Preventivo</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 flex items-center gap-4">
              <Sparkles className="w-12 h-12" />
              Mantenimiento Preventivo
            </h1>
            <p className="text-xl text-sky-100 mb-6">
              Alarga la vida de tu consola con limpieza profesional y cambio de pasta térmica. 
              Previene averías antes de que ocurran.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <Thermometer className="w-4 h-4" />
                <span className="text-sm">Reduce temperaturas</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <Wind className="w-4 h-4" />
                <span className="text-sm">Menos ruido</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-sm">Mayor durabilidad</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Services */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold mb-8 text-center">Servicios de Mantenimiento</h2>
        
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-500"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => {
              const Icon = getIcon(service.id);
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className={`bg-white rounded-2xl border-2 p-6 hover:shadow-xl transition-all cursor-pointer ${
                    service.id === 'mantenimiento_full' ? 'border-sky-500 relative' : 'border-gray-200'
                  }`}
                  onClick={() => selectService(service)}
                  data-testid={`maintenance-service-${service.id}`}
                >
                  {service.id === 'mantenimiento_full' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sky-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      MEJOR VALOR
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-sky-600" />
                    </div>
                    <span className="text-2xl font-bold text-sky-600">{service.price}</span>
                  </div>
                  
                  <h3 className="text-lg font-semibold mb-2">{service.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                  
                  <ul className="space-y-2 mb-4">
                    {service.includes.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="flex items-center justify-between text-sm border-t border-gray-100 pt-4">
                    <span className="text-gray-500">Tiempo: {service.time}</span>
                    <ArrowRight className="w-5 h-5 text-sky-500" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Why Maintenance */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-center">¿Por Qué Hacer Mantenimiento?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-xl text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Thermometer className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="font-semibold mb-2">Reduce Temperaturas</h3>
              <p className="text-sm text-gray-600">
                La pasta térmica seca causa sobrecalentamiento. Cambiarla puede reducir 15-20°C.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Wind className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="font-semibold mb-2">Menos Ruido</h3>
              <p className="text-sm text-gray-600">
                Ventiladores limpios giran más despacio y hacen menos ruido.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="font-semibold mb-2">Previene Averías</h3>
              <p className="text-sm text-gray-600">
                El polvo y calor excesivo son las principales causas de fallos de hardware.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-purple-500" />
              </div>
              <h3 className="font-semibold mb-2">Mayor Durabilidad</h3>
              <p className="text-sm text-gray-600">
                Mantenimiento regular puede duplicar la vida útil de tu consola.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full"
          >
            <h2 className="text-xl font-bold mb-2">Solicitar Mantenimiento</h2>
            {selectedService && (
              <p className="text-sky-600 text-sm mb-4">{selectedService.name} - {selectedService.price}</p>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Tipo de dispositivo *</label>
                <select
                  required
                  value={formData.device_type}
                  onChange={(e) => setFormData({...formData, device_type: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg p-2"
                >
                  <option value="">Seleccionar...</option>
                  <option value="PS5">PlayStation 5</option>
                  <option value="PS4">PlayStation 4</option>
                  <option value="PS4 Pro">PlayStation 4 Pro</option>
                  <option value="Xbox Series X">Xbox Series X</option>
                  <option value="Xbox One">Xbox One</option>
                  <option value="Nintendo Switch">Nintendo Switch</option>
                </select>
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Modelo específico</label>
                <Input
                  placeholder="Ej: PS5 Disc Edition CFI-1016A"
                  value={formData.device_model}
                  onChange={(e) => setFormData({...formData, device_model: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Tu email *</label>
                <Input
                  type="email"
                  required
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Tu teléfono *</label>
                <Input
                  type="tel"
                  required
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Fecha preferida (opcional)</label>
                <Input
                  type="date"
                  value={formData.preferred_date}
                  onChange={(e) => setFormData({...formData, preferred_date: e.target.value})}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-sky-600 hover:bg-sky-700">
                  Enviar Solicitud
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowForm(false)}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default MaintenancePage;
